import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Entity } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { GridLoader, useAuth, useReady } from '@shapeable/ui';

const cls = classNames('ai-chat-interface-modal');

const SHAPE_AI_APP_URL = 'https://gesda-ai-next-client.netlify.app/';

// -------- Types -------->

// -------- Props -------->

export type AiChatInterfaceModalProps = Classable & HasChildren & { 
  entity?: Entity;
};

export const AiChatInterfaceModalDefaultProps: Omit<AiChatInterfaceModalProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ModalContainerStyles = breakpoints({
  base: css`
    z-index: 500;
    min-width: 280px;
    background: ${theme.COLOR('canvas')};
    box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.15), 0 12px 16px rgba(0, 0, 0, 0.3);
    position: fixed;
    top: 64px;
    bottom: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
    cursor: default;
    padding-bottom: env(safe-area-inset-bottom, 0);
    box-sizing: border-box;

    iframe {
      flex: 1 1 auto;
      min-height: 0;
      width: 100vw;
      height: auto !important;
      overflow-x: hidden;
      border: none;
      padding-top: 20px;
      box-sizing: border-box;
    }
  `,
  landscape: css`
    iframe {
      width: 90vw;
    }
  `,
  tablet: css`
    left: 5vw;
    iframe {
      width: 85vw;
    }
  `,
  desktop: css`
    left: 13.8vw;
    iframe {
      width: 70vw;
    }
  `,
});

const LoaderWrapStyles = breakpoints({
  base: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 20px;
    pointer-events: none;
    text-align: center;
  `,
});

const LoaderStyles = breakpoints({
  base: css`
    
  `,
});


// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ModalContainerStyles}`,
  LoaderWrap: styled.div`${LoaderWrapStyles}`,
  Loader: styled(GridLoader)`${LoaderStyles}`,
};

export const AiChatInterfaceModal: Shapeable.FC<AiChatInterfaceModalProps> = (props) => {
  const { className, children, entity } = props;

  // const auth = useAuth();
  const isReady = useReady();
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  React.useEffect(() => {
    if (!isReady) {
      setIsIframeLoaded(false);
    }
  }, [isReady]);

    React.useEffect(() => {
      const handleMessage = (event: MessageEvent) => {
      if (!event.data || event.data.type !== 'SHAPE_AI_REQUEST_IFRAME_DATA') {
        return; // Ignore third party tools and extensions
      }

      // Find the iframe element on the page
      const iframe = document.getElementById('ai-next-iframe') as HTMLIFrameElement;

      // Security: Ensure the message is from our specific iframe
      if (!iframe || event.source !== iframe.contentWindow) {
        
        return;
      }

      // Check if the iframe is asking for the origin
      if (event.data && event.data.type === 'SHAPE_AI_REQUEST_IFRAME_DATA') {

        iframe.contentWindow.postMessage({
          type: 'SHAPE_AI_IFRAME_DATA',
          origin: window.location.origin,
        }, SHAPE_AI_APP_URL);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [SHAPE_AI_APP_URL]);

  const showLoader = !isReady || !isIframeLoaded;

  return (
    <My.Container className={cls.name(className)}>
      {showLoader && (
        <My.LoaderWrap>
          <My.Loader message="Preparing your RadarAI assistant" spinnerSize={48} />
        </My.LoaderWrap>
      )}
      { 
        isReady && (
          <iframe 
            id="ai-next-iframe"
            src={SHAPE_AI_APP_URL}
            title="RadarAI"
            onLoad={() => setIsIframeLoaded(true)}
          />
        )
      }
    </My.Container>
    
  )
};

AiChatInterfaceModal.cls = cls;
